import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MessageCircle,
  RefreshCw,
  AlertCircle,
  Search,
  UserPlus,
  ExternalLink,
  X,
  Filter,
  Link as LinkIcon,
  Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import AppShell from "@/components/layout/AppShell";
import WhatsAppConversation from "@/components/crm/WhatsAppConversation";
import PhoneLookupDialog from "@/components/crm/PhoneLookupDialog";
import { Phone as PhoneIcon } from "lucide-react";
import { whatsappApi, leadsApi } from "@/lib/crmApi";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { formatDateTime, getInitials } from "@/lib/crmHelpers";

// Pretty-print a phone number. We only special-case Senegalese numbers
// (+221 + 9 digits) since that's the company's market — everything else
// just gets a "+" prefix to disambiguate from raw LIDs.
function formatPhone(p) {
  if (!p) return "";
  const d = String(p).replace(/[^0-9]/g, "");
  if (!d) return p;
  if (d.length === 12 && d.startsWith("221")) {
    return `+221 ${d.slice(3, 5)} ${d.slice(5, 8)} ${d.slice(8, 10)} ${d.slice(10, 12)}`;
  }
  if (d.length === 9) {
    return `+221 ${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 7)} ${d.slice(7, 9)}`;
  }
  // 13–15 digits = WhatsApp LID, render as-is to make the privacy ID obvious
  return d.length >= 13 ? d : `+${d}`;
}

// True for raw WhatsApp privacy LIDs (no carrier-issued phone).
function isLid(p) {
  const d = String(p || "").replace(/[^0-9]/g, "");
  return d.length >= 13;
}

// Extract the dialable phone number from a Baileys JID like
// "221778715171:42@s.whatsapp.net". Strips the device suffix and domain.
function jidToPhone(jid) {
  if (!jid) return "";
  return String(jid).split("@")[0].split(":")[0];
}

export default function InboxPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // v1.8.2 — filtre "Programme" sur l'inbox = admin uniquement.
  // Les commerciaux sont déjà scopés à leur projet actif côté serveur,
  // un dropdown qui leur permettrait de "choisir un programme" est
  // trompeur et inutile.
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [programmeFilter, setProgrammeFilter] = useState("all");
  const [commercialFilter, setCommercialFilter] = useState("all");
  const [budgetFilter, setBudgetFilter] = useState("all");
  // Connected WhatsApp number for the currently-scoped session. Surfaced
  // in the header so the user always knows WHICH phone is being used to
  // display these conversations — no more guessing whether you're looking
  // at the global number, the Almadies number, or RYAD Mermoz's.
  const [sessionStatus, setSessionStatus] = useState(null);
  // Active project for label purposes — name + id.
  const activeProjetId = user?.current_projet || null;
  const activeProjet = useMemo(() => {
    const list = user?.available_projets || user?.available_programmes || [];
    return list.find((p) => p.id === activeProjetId) || null;
  }, [user, activeProjetId]);

  // Right-side conversation panel
  const [openConvo, setOpenConvo] = useState(null);  // {phone, name, lead_id?}
  // Admin "lookup all messages by phone" dialog — opened from the header
  const [phoneLookupOpen, setPhoneLookupOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Bumped from 50 → 100 (sidecar cap) so each project's inbox shows
       // as many of its number's recent conversations as the engine
       // exposes. The user request "afficher les 50 derniers messages
       // sur chaque numéro" implied 50 was a floor, not a ceiling — we
       // give them the full window the backend allows.
      const data = await whatsappApi.inbox(100);
      setItems(data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.humanMessage || "Service WhatsApp indisponible");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  // Probe the WhatsApp session that's currently scoping this inbox so we
  // can display the connected number. Re-runs whenever the user switches
  // active project (cookie → activeProjetId in useAuth).
  useEffect(() => {
    let cancelled = false;
    whatsappApi
      .status(activeProjetId || null)
      .then((s) => {
        if (!cancelled) setSessionStatus(s);
      })
      .catch(async () => {
        // Project session may not be started yet → 404. Fall back to the
        // default session so we still show SOMETHING (the global number).
        if (!activeProjetId) {
          if (!cancelled) setSessionStatus(null);
          return;
        }
        try {
          const s = await whatsappApi.status(null);
          if (!cancelled) setSessionStatus(s);
        } catch {
          if (!cancelled) setSessionStatus(null);
        }
      });
    return () => { cancelled = true; };
  }, [activeProjetId]);

  // Build distinct values for the filter dropdowns from the lead metadata
  // we already have on each conversation. Empty values are dropped so the
  // dropdowns don't show "(empty)" entries.
  const filterOptions = useMemo(() => {
    const programmes = new Set();
    const commerciaux = new Set();
    const budgets = new Set();
    items.forEach((it) => {
      if (it.lead?.programme_immobilier) programmes.add(it.lead.programme_immobilier);
      if (it.lead?.commercial) commerciaux.add(it.lead.commercial);
      if (it.lead?.budget) budgets.add(it.lead.budget);
    });
    return {
      programmes: [...programmes].sort(),
      commerciaux: [...commerciaux].sort(),
      budgets: [...budgets].sort(),
    };
  }, [items]);

  const filtered = items.filter((it) => {
    // Programme / Commercial / Budget — require an exact match against the
    // LINKED LEAD. Conversations with no lead are kept on "all" but excluded
    // as soon as the user selects any specific filter (they have no value to
    // match).
    if (programmeFilter !== "all" && it.lead?.programme_immobilier !== programmeFilter) return false;
    if (commercialFilter !== "all" && it.lead?.commercial !== commercialFilter) return false;
    if (budgetFilter !== "all" && it.lead?.budget !== budgetFilter) return false;

    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const qDigits = q.replace(/[^0-9]/g, "");
    // Use `contact_pushname` (freshest inbound pushname across the whole
    // thread, computed server-side) so we find a contact even when the
    // LATEST message is one we sent. Fall back to the last_message pushname
    // only when it's an inbound (the outgoing pushname is the BOT owner's
    // WA display name → would match every conversation we ever sent to).
    const inboundPushname =
      it.contact_pushname ||
      (it.last_message?.direction === "in" ? it.last_message?.pushname : "");
    // Phone matching: include `phone_pn` (real number behind a LID) so
    // typing the contact's actual phone finds a LID-based conversation.
    // Strip non-digit chars on both sides so "+221 77..." matches "22177...".
    const itPhoneDigits = String(it.phone || "").replace(/[^0-9]/g, "");
    const itPnDigits = String(it.phone_pn || "").replace(/[^0-9]/g, "");
    const leadTelDigits = String(it.lead?.telephone || "").replace(/[^0-9]/g, "");
    return (
      (it.lead?.nom || "").toLowerCase().includes(q) ||
      (it.last_message?.lead_nom || "").toLowerCase().includes(q) ||
      (inboundPushname || "").toLowerCase().includes(q) ||
      (qDigits && leadTelDigits.includes(qDigits)) ||
      (qDigits && itPhoneDigits.includes(qDigits)) ||
      (qDigits && itPnDigits.includes(qDigits))
    );
  });

  const totalUnread = items.reduce((acc, it) => acc + (it.unread || 0), 0);
  const hasActiveFilter =
    programmeFilter !== "all" ||
    commercialFilter !== "all" ||
    budgetFilter !== "all" ||
    !!search.trim();

  const resetFilters = () => {
    setSearch("");
    setProgrammeFilter("all");
    setCommercialFilter("all");
    setBudgetFilter("all");
  };

  const onConversationClick = (c) => {
    const m = c.last_message || {};
    // `pushname` on an OUTGOING message is the bot owner's WhatsApp display
    // name (e.g. "Commercial Sogi"), not the recipient's. We must NOT use it
    // as the conversation name — that would label the chat after ourselves.
    // `c.contact_pushname` is the freshest INBOUND pushname across the whole
    // thread (computed server-side), so it works even when the latest message
    // is outgoing — falls back to the legacy in-only check for older payloads.
    const contactPushname =
      c.contact_pushname || (m.direction === "in" ? m.pushname : null);
    // Prefer the lead's actual data over what WhatsApp sent us — the lead's
    // phone is the carrier number, not the privacy LID; the lead's nom is
    // what the user entered manually, not WhatsApp's pushName.
    // `phone_pn` is the real WhatsApp number Baileys leaks for hidden contacts;
    // we use it as a fallback when the lead doesn't carry a telephone yet.
    // Which WhatsApp session owns this conversation? The last message carries
    // the `session_id` that actually handled it (== projet_id by convention;
    // "default" for the global session). We propagate it so the conversation
    // panel probes the RIGHT session for its "Connecté" indicator — otherwise
    // it falls back to the default session (often disconnected in a per-project
    // multi-number setup) and wrongly shows "Service non connecté".
    const convoProjetId =
      (m.session_id && m.session_id !== "default" ? m.session_id : null) ||
      c.lead?.projet_id ||
      null;
    setOpenConvo({
      phone: c.lead?.telephone || c.phone_pn || c.phone,
      jidPhone: c.phone, // raw inbox phone (may be a LID); used to query messages
      phone_pn: c.phone_pn || null, // raw real phone if known (for the edit pencil)
      name: c.lead?.nom || m.lead_nom || contactPushname || "Inconnu",
      lead_id: c.lead?.id || m.lead_id || null,
      lead_nom: c.lead?.nom || m.lead_nom,
      projet_id: convoProjetId,
    });
  };

  return (
    <AppShell topbarProps={{ showSearch: false, showAdd: false, onRefresh: load }}>
      <div className="mx-auto max-w-[1100px] px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-label text-muted-foreground">Conversations</p>
            <h1 className="font-heading flex items-center gap-2 text-2xl font-bold tracking-tight sm:text-3xl">
              <MessageCircle className="h-7 w-7 text-emerald-600" />
              Inbox WhatsApp
              {totalUnread > 0 && (
                <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-bold text-white">
                  {totalUnread} non lus
                </span>
              )}
            </h1>
            <p className="text-sm text-muted-foreground">
              {loading && items.length === 0
                ? "Chargement…"
                : `${items.length} conversation${items.length > 1 ? "s" : ""} récente${items.length > 1 ? "s" : ""} · les 100 plus récentes du numéro courant`}
            </p>
            {/* WhatsApp session indicator — show the connected number used
                to display these conversations. Helps the user understand
                why the inbox looks different across projects (each project
                has its own WhatsApp number since v1.6.0). */}
            <div
              className="mt-2 inline-flex flex-wrap items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50/70 px-3 py-1 text-xs dark:border-emerald-900/40 dark:bg-emerald-950/30"
              data-testid="inbox-session-indicator"
            >
              {(() => {
                const connected =
                  sessionStatus &&
                  ["connected", "ready", "open"].includes(sessionStatus.status);
                const phone = jidToPhone(sessionStatus?.me || "");
                const projetLabel =
                  activeProjet?.nom ||
                  (activeProjetId ? "Projet sélectionné" : "Tous les projets");
                return (
                  <>
                    <span
                      className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        connected ? "bg-emerald-500 animate-pulse" : "bg-zinc-400"
                      )}
                      title={connected ? "Connecté" : "Déconnecté"}
                    />
                    <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                      {projetLabel}
                    </span>
                    <span className="text-muted-foreground">·</span>
                    {phone ? (
                      <span className="inline-flex items-center gap-1 font-mono tabular-nums">
                        <PhoneIcon className="h-3 w-3 text-emerald-600" />
                        {formatPhone(phone)}
                      </span>
                    ) : (
                      <span className="italic text-muted-foreground">
                        Aucun numéro pairé
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* "Recherche par numéro" — admin tool to pull every message
                ever exchanged with a phone, regardless of how the
                conversations were grouped in the inbox. */}
            <Button
              onClick={() => setPhoneLookupOpen(true)}
              variant="outline"
              size="sm"
              className="gap-1.5 transition-transform active:scale-[0.96]"
              data-testid="inbox-phone-lookup-btn"
            >
              <PhoneIcon className="h-3.5 w-3.5 text-emerald-600" />
              Recherche par numéro
            </Button>
            <Button
              onClick={load}
              variant="outline"
              size="sm"
              className="gap-1.5 transition-transform active:scale-[0.96]"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
              Actualiser
            </Button>
          </div>
        </div>

        {/* Search + filtres (programme / commercial / budget) */}
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1 lg:max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom ou numéro…"
              className="h-10 pl-9"
              data-testid="inbox-search"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Filter className="h-3.5 w-3.5 flex-none text-muted-foreground" />
            {/* Filtre Programme — admin uniquement (v1.8.2). */}
            {isAdmin && (
              <Select value={programmeFilter} onValueChange={setProgrammeFilter}>
                <SelectTrigger className="h-10 w-[180px]" data-testid="inbox-filter-programme">
                  <SelectValue placeholder="Programme" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous programmes</SelectItem>
                  {filterOptions.programmes.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={commercialFilter} onValueChange={setCommercialFilter}>
              <SelectTrigger className="h-10 w-[160px]" data-testid="inbox-filter-commercial">
                <SelectValue placeholder="Commercial" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous commerciaux</SelectItem>
                {filterOptions.commerciaux.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={budgetFilter} onValueChange={setBudgetFilter}>
              <SelectTrigger className="h-10 w-[150px]" data-testid="inbox-filter-budget">
                <SelectValue placeholder="Budget" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous budgets</SelectItem>
                {filterOptions.budgets.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasActiveFilter && (
              <Button
                onClick={resetFilters}
                variant="ghost"
                size="sm"
                className="h-10 gap-1.5 text-xs text-muted-foreground"
                data-testid="inbox-filter-reset"
              >
                <X className="h-3 w-3" />
                Réinitialiser
              </Button>
            )}
          </div>
        </div>

        {/* Filtered count chip */}
        {hasActiveFilter && filtered.length !== items.length && (
          <p className="mb-3 text-xs text-muted-foreground">
            {filtered.length} sur {items.length} conversation{items.length > 1 ? "s" : ""}
          </p>
        )}

        {error ? (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 dark:border-amber-900/40 dark:bg-amber-950/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 flex-none text-amber-600" />
              <div>
                <p className="text-sm font-semibold">WhatsApp non connecté</p>
                <p className="mt-1 text-xs text-muted-foreground">{error}</p>
                <Button
                  onClick={() => navigate("/parametres")}
                  variant="link"
                  size="sm"
                  className="mt-2 h-auto p-0 text-xs"
                >
                  Aller dans Paramètres → WhatsApp
                </Button>
              </div>
            </div>
          </div>
        ) : loading && items.length === 0 ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-20 animate-shimmer rounded-xl border border-border/60" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-border/60 p-10 text-center">
            <MessageCircle className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="mt-3 font-heading text-base font-semibold">
              {search ? "Aucune conversation correspondante" : "Aucune conversation"}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {search
                ? "Essayez un autre mot-clé."
                : "Les conversations apparaîtront ici dès que vous écrirez à un lead ou recevrez un message."}
            </p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="inbox-list">
            {filtered.map((c) => (
              <ConversationCard
                key={c.phone}
                item={c}
                onClick={() => onConversationClick(c)}
                onOpenLead={(leadId) => navigate(`/leads/${leadId}`)}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Conversation panel (right Sheet) */}
      <ConversationSheet
        convo={openConvo}
        onClose={() => { setOpenConvo(null); load(); }}
        onLeadCreated={() => { setOpenConvo(null); load(); }}
      />

      {/* Admin lookup-by-phone dialog */}
      <PhoneLookupDialog
        open={phoneLookupOpen}
        onOpenChange={setPhoneLookupOpen}
      />
    </AppShell>
  );
}

/* ---------------------------------------------------------------- */

function ConversationCard({ item, onClick, onOpenLead }) {
  const m = item.last_message || {};
  // Prefer the server-computed `contact_pushname` — it's the freshest INBOUND
  // pushname across the whole conversation, so it survives outgoing-only
  // threads. Falls back to the legacy direction check for older payloads.
  const lastInboundPushname = m.direction === "in" ? m.pushname : null;
  const contactPushname = item.contact_pushname || lastInboundPushname;
  // Display priority for the phone:
  //   1. Linked lead's stored telephone (always wins — user-curated value)
  //   2. phone_pn from WhatsApp (real number behind a privacy LID)
  //   3. raw inbox phone (the LID itself, last resort)
  const name = item.lead?.nom || m.lead_nom || contactPushname || "Inconnu";
  const realPhone = item.lead?.telephone || item.phone_pn || item.phone;
  const isUnread = item.unread > 0;
  const hasLead = !!(item.lead?.id || m.lead_id);
  const leadId = item.lead?.id || m.lead_id || null;
  // Only show the LID badge when we genuinely have nothing better to display
  const showLid = isLid(item.phone) && !item.lead?.telephone && !item.phone_pn;

  return (
    <li
      data-testid={`inbox-item-${item.phone}`}
      onClick={onClick}
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-all hover:-translate-y-0.5 hover:shadow-soft active:translate-y-0",
        isUnread
          ? "border-emerald-300 bg-emerald-50/40 dark:border-emerald-900/40 dark:bg-emerald-950/20"
          : "border-border/60 bg-card"
      )}
    >
      <div className={cn(
        "flex h-10 w-10 flex-none items-center justify-center rounded-full font-semibold",
        hasLead ? "bg-accent-navy text-white" : "bg-muted text-muted-foreground"
      )}>
        {getInitials(name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="font-heading truncate text-sm font-bold">
            {name}
            {!hasLead && (
              <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium uppercase text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
                Pas un lead
              </span>
            )}
          </span>
          <div className="flex flex-none items-center gap-2">
            {/* Direct shortcut to the lead detail page — only shown when linked */}
            {hasLead && leadId && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenLead?.(leadId); }}
                title="Voir la fiche du lead"
                className="rounded-full p-1 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                data-testid={`inbox-open-lead-${leadId}`}
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </button>
            )}
            <span className="text-[10px] text-muted-foreground">
              {formatDateTime(m.timestamp)}
            </span>
          </div>
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          {showLid ? (
            <>
              {/* WhatsApp privacy LID — the displayed digits are NOT a phone
                  number, so we hide them and tell the user how to provide one.
                  The pencil icon inside the conversation panel lets them set
                  the real phone manually. */}
              <span className="italic text-muted-foreground">Numéro privé</span>
              <span className="rounded bg-amber-100 px-1 py-px text-[9px] font-medium uppercase text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
                LID
              </span>
              <span
                className="text-[10px] text-emerald-600 underline-offset-2 hover:underline cursor-pointer"
                title="Ouvre la conversation pour saisir le numéro réel"
              >
                · ajouter le numéro
              </span>
            </>
          ) : (
            <span className="font-mono">{formatPhone(realPhone)}</span>
          )}
        </div>
        {/* Optional lead metadata chips — only when we actually know them */}
        {(item.lead?.programme_immobilier || item.lead?.commercial || item.lead?.budget) && (
          <div className="mt-1 flex flex-wrap gap-1">
            {item.lead?.programme_immobilier && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
                {item.lead.programme_immobilier}
              </span>
            )}
            {item.lead?.commercial && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
                👤 {item.lead.commercial}
              </span>
            )}
            {item.lead?.budget && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
                💰 {item.lead.budget}
              </span>
            )}
          </div>
        )}
        <div className="mt-1 flex items-start justify-between gap-2">
          <p className={cn(
            "line-clamp-1 text-sm",
            isUnread ? "font-semibold text-foreground" : "text-muted-foreground"
          )}>
            {m.direction === "out" && <span className="font-bold text-emerald-600">Vous : </span>}
            {m.body
              ? m.body
              : m.media_type
                ? `[${m.media_type}]`
                // No body + no media_type = notification / reaction / call
                // event. Replace the cryptic "(message vide)" placeholder
                // with something more meaningful that makes clear the row
                // is not a bug — just a non-text WhatsApp event.
                : <span className="italic">(notification WhatsApp)</span>}
          </p>
          {item.unread > 0 && (
            <span className="flex-none rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
              {item.unread}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

/* ---------------------------------------------------------------- */

function ConversationSheet({ convo, onClose, onLeadCreated }) {
  const navigate = useNavigate();
  const [creatingLead, setCreatingLead] = useState(false);
  const [linkingOpen, setLinkingOpen] = useState(false);

  if (!convo) return null;

  const isLinked = !!convo.lead_id;
  // The displayed phone is the lead's real number when known. The "JID phone"
  // is the inbox key — what onIncomingMessage stored as `phone` (which is the
  // LID for privacy chats) — and that's what we must use to query messages
  // from this exact conversation.
  const displayPhone = convo.phone;
  const isLidConvo = isLid(convo.jidPhone || convo.phone);

  // Synthetic "lead" object to feed WhatsAppConversation, even when there's
  // no real lead in DB yet (the component now supports phone-only mode).
  // We pass the JID phone (raw, possibly LID) so the messages query matches
  // what's stored in wa_messages, but we display the real phone.
  const fauxLead = {
    id: convo.lead_id,           // null for INCONNU conversations
    telephone: convo.jidPhone || convo.phone,
    nom: convo.name,
    programme_immobilier: "",
    // Carry the owning project so WhatsAppConversation probes that project's
    // session (its dedicated number) instead of the default one.
    projet_id: convo.projet_id || null,
  };

  const createLeadFromPhone = async () => {
    if (!window.confirm(`Créer un lead pour ${convo.name} (${formatPhone(displayPhone)}) ?`)) return;
    setCreatingLead(true);
    try {
      const created = await leadsApi.create({
        nom: convo.name && convo.name !== "Inconnu" ? convo.name : `WhatsApp ${displayPhone}`,
        telephone: displayPhone,
        notes: "Lead créé automatiquement depuis l'inbox WhatsApp",
        statut: "nouveau",
      });
      toast.success(`Lead "${created.nom}" créé`);
      onLeadCreated?.();
      navigate(`/leads/${created.id}`);
    } catch (e) {
      toast.error(e?.humanMessage || "Erreur lors de la création");
    } finally {
      setCreatingLead(false);
    }
  };

  return (
    // modal={false} so the sidebar / sub-nav remain clickable while this
    // Sheet is open. Default Radix behaviour traps focus + blocks pointer
    // events outside the panel, which prevented users from navigating to
    // Calendrier / Leads while a conversation was on screen. The Sheet
    // still closes on Escape or by clicking its dedicated close button.
    <Sheet open={!!convo} onOpenChange={(o) => !o && onClose?.()} modal={false}>
      <SheetContent
        side="right"
        className="w-full p-0 sm:max-w-[640px] overflow-hidden flex flex-col"
        data-testid="inbox-conversation-sheet"
        // Block the backdrop's pointer-events:none leak in non-modal mode:
        // when the user clicks anywhere outside the panel, do nothing
        // (don't close). They close it explicitly via the X / Esc.
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <SheetHeader className="border-b border-border/60 bg-emerald-50/50 px-5 py-4 dark:bg-emerald-950/20">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className={cn(
                "flex h-10 w-10 flex-none items-center justify-center rounded-full text-sm font-semibold",
                isLinked ? "bg-accent-navy text-white" : "bg-muted text-muted-foreground"
              )}>
                {getInitials(convo.name)}
              </div>
              <div className="min-w-0">
                <SheetTitle className="truncate text-left">{convo.name}</SheetTitle>
                <SheetDescription className="flex flex-wrap items-center gap-1.5 text-left">
                  <span className="font-mono">{formatPhone(displayPhone)}</span>
                  {/* Pencil to manually set the real phone behind a privacy LID.
                      Only visible when the displayed value is the raw LID
                      (no linked lead AND no phone_pn known yet). */}
                  {isLidConvo && !isLinked && (
                    <button
                      type="button"
                      onClick={async () => {
                        const current = convo.phone_pn || "";
                        const v = window.prompt(
                          "Saisissez le vrai numéro de téléphone du contact (ex : +221 77 123 45 67) :",
                          current
                        );
                        if (v == null) return;
                        const cleaned = String(v).replace(/[^0-9]/g, "");
                        if (!cleaned) {
                          toast.error("Numéro invalide");
                          return;
                        }
                        try {
                          await whatsappApi.setPhonePn({
                            phone: convo.jidPhone || convo.phone,
                            phonePn: cleaned,
                          });
                          toast.success("Numéro enregistré");
                          onLeadCreated?.(); // triggers inbox refresh + close
                        } catch (e) {
                          toast.error(e?.humanMessage || "Erreur");
                        }
                      }}
                      title="Saisir le vrai numéro de téléphone"
                      className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                      data-testid="inbox-edit-phone"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  )}
                  {isLidConvo && !isLinked && (
                    <span className="rounded bg-amber-100 px-1 py-px text-[9px] font-bold uppercase text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
                      LID
                    </span>
                  )}
                  {!isLinked && (
                    <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
                      Pas un lead
                    </span>
                  )}
                </SheetDescription>
              </div>
            </div>
            <div className="flex flex-none gap-1">
              {isLinked && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => navigate(`/leads/${convo.lead_id}`)}
                  data-testid="inbox-open-lead"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Voir le lead
                </Button>
              )}
              {!isLinked && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => setLinkingOpen(true)}
                    data-testid="inbox-link-lead"
                    title="Associer cette conversation à un lead existant"
                  >
                    <LinkIcon className="h-3.5 w-3.5" />
                    Associer
                  </Button>
                  <Button
                    size="sm"
                    className="gap-1.5"
                    onClick={createLeadFromPhone}
                    disabled={creatingLead}
                    data-testid="inbox-create-lead"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    Créer
                  </Button>
                </>
              )}
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-3">
          <WhatsAppConversation lead={fauxLead} />
        </div>
      </SheetContent>

      {/* Association lead existant — only mounted when linkingOpen is true so
          the picker doesn't fetch the leads list eagerly on every Sheet open. */}
      {linkingOpen && (
        <LinkLeadDialog
          phone={convo.jidPhone || convo.phone}
          conversationName={convo.name}
          onClose={() => setLinkingOpen(false)}
          onLinked={(lead) => {
            toast.success(`Conversation associée à ${lead.nom}`);
            setLinkingOpen(false);
            onLeadCreated?.();
          }}
        />
      )}
    </Sheet>
  );
}

/* ---------------------------------------------------------------- */

function LinkLeadDialog({ phone, conversationName, onClose, onLinked }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    leadsApi
      .list()
      .then((l) => { if (!cancelled) setLeads(Array.isArray(l) ? l : []); })
      .catch(() => { if (!cancelled) setLeads([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads.slice(0, 50);
    return leads
      .filter((l) =>
        `${l.nom || ""} ${l.telephone || ""} ${l.email || ""}`
          .toLowerCase()
          .includes(q)
      )
      .slice(0, 50);
  }, [leads, search]);

  const handleLink = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    try {
      const r = await whatsappApi.linkLead({ phone, leadId: selectedId });
      if (r?.lead) onLinked?.(r.lead);
      else onLinked?.({ nom: "lead" });
    } catch (e) {
      toast.error(e?.humanMessage || "Erreur lors de l'association");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-[500px]" data-testid="link-lead-dialog">
        <DialogHeader>
          <DialogTitle>Associer la conversation</DialogTitle>
          <DialogDescription>
            Recherchez un lead existant pour lier la conversation
            {conversationName && ` avec ${conversationName}`}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nom, téléphone, email…"
              className="h-10 pl-9"
              data-testid="link-lead-search"
            />
          </div>

          <div className="max-h-[280px] overflow-y-auto rounded-lg border border-border/60 bg-background">
            {loading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                Chargement des leads…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {search ? "Aucun lead correspondant" : "Aucun lead disponible"}
              </div>
            ) : (
              <ul className="divide-y divide-border/60">
                {filtered.map((l) => (
                  <li
                    key={l.id}
                    onClick={() => setSelectedId(l.id)}
                    className={cn(
                      "cursor-pointer px-3 py-2 text-sm transition-colors",
                      selectedId === l.id
                        ? "bg-emerald-50 dark:bg-emerald-950/30"
                        : "hover:bg-muted"
                    )}
                    data-testid={`link-lead-option-${l.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-medium">{l.nom || "(sans nom)"}</span>
                      {selectedId === l.id && (
                        <span className="text-[10px] font-bold uppercase text-emerald-600">
                          ✓ Sélectionné
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                      {l.telephone || "—"}
                      {l.programme_immobilier && ` · ${l.programme_immobilier}`}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={handleLink}
            disabled={!selectedId || submitting}
            data-testid="link-lead-confirm"
          >
            {submitting ? "Association…" : "Associer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
